import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Container,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import SearchBar from '../../components/SearchBar';
import { getTenantDisplayName } from '../../common/tenantDisplayNames';
import TenantFilterMenu from './TenantFilterMenu';
import { MasterListState } from './useMasterListState';

export interface MasterColumn {
    label: string;
    /** When set, the column header is a sort toggle for this key. */
    sortKey?: string;
}

interface MasterListScaffoldProps {
    controls: MasterListState;
    title: string;
    /** Count / summary line under the title. */
    subtitle: ReactNode;
    searchPlaceholder: string;
    columns: MasterColumn[];
    /** Row count for the pagination footer (the full, unpaged list). */
    totalCount: number;
    /** Number of tenants whose data has finished loading. */
    loadedTenants: number;
    /** Total number of tenants being queried. */
    totalTenants: number;
    /** Slugs whose per-tenant query failed — rendered as error rows. */
    failedTenants: string[];
    /** When true, an empty-state row is shown instead of `children`. */
    showEmptyState: boolean;
    /** Message for the empty-state row. */
    emptyMessage: string;
    /** The per-tenant `TenantQuerySlot` fan-out (page-specific). */
    querySlots: ReactNode;
    /** Refetches the page's per-tenant data (cache invalidation) —
     *  rendered as a reload icon button next to the tenant filter. */
    onReload?: () => void;
    /** The data rows (page-specific); wrapped in a shared `<TableBody>`. */
    children: ReactNode;
}

/**
 * Shared page chrome for the all-tenant (Master Mode) list pages: the
 * header + search bars, the tenant filter, the loading indicator and the
 * table card (head + pagination). The three master pages differ only in
 * their columns and row rendering, which stay in the page via `columns`
 * and `children` (the `<TableBody>`); everything else lives here once so
 * it isn't duplicated three times.
 */
const MasterListScaffold: React.FC<MasterListScaffoldProps> = ({
    controls,
    title,
    subtitle,
    searchPlaceholder,
    columns,
    totalCount,
    loadedTenants,
    totalTenants,
    failedTenants,
    showEmptyState,
    emptyMessage,
    querySlots,
    onReload,
    children,
}) => {
    const { t } = useTranslation();

    const renderSearchBar = (className?: string) => (
        <SearchBar
            className={className}
            placeholder={searchPlaceholder}
            value={controls.searchQuery}
            onChange={controls.setSearchQuery}
        />
    );

    return (
        <Container className={'px-0 sm:px-4'} maxWidth={false}>
            <Box sx={{ py: 3 }}>
                <PageHeader>
                    <div className={'grow'}>
                        <PageHeadline>{title}</PageHeadline>
                        <p className={'text-sm text-foreground/60'}>{subtitle}</p>
                    </div>
                    {renderSearchBar('hidden sm:block')}
                </PageHeader>

                <div className={'sm:hidden mb-3'}>{renderSearchBar()}</div>

                {querySlots}

                <div className={'mb-3 flex items-center gap-1'}>
                    <TenantFilterMenu controls={controls} />
                    {onReload && (
                        <Tooltip title={t('master.reload')}>
                            <IconButton
                                size={'small'}
                                onClick={onReload}
                                aria-label={t('master.reload')}
                                className={'text-foreground'}
                            >
                                <RefreshIcon fontSize={'small'} />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                {loadedTenants < totalTenants && totalTenants > 0 && (
                    <p className={'mb-3 text-sm text-foreground/60'}>
                        {t('master.loading_progress', {
                            loaded: loadedTenants,
                            total: totalTenants,
                        })}
                    </p>
                )}

                <div className={'bg-text-contrast rounded-[5px] shadow-sm overflow-x-auto'}>
                    <Table
                        size={'small'}
                        sx={{
                            '& thead th': {
                                whiteSpace: 'nowrap',
                                verticalAlign: 'top',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                {columns.map((column, index) => (
                                    <TableCell key={column.sortKey ?? `col-${index}`}>
                                        {column.sortKey ? (
                                            <TableSortLabel
                                                active={controls.sortState.key === column.sortKey}
                                                direction={
                                                    controls.sortState.order === 'desc'
                                                        ? 'desc'
                                                        : 'asc'
                                                }
                                                onClick={() => controls.toggleSort(column.sortKey as string)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        ) : (
                                            column.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {failedTenants.map((tenant) => (
                                <TableRow key={`failed-${tenant}`}>
                                    <TableCell>{getTenantDisplayName(tenant)}</TableCell>
                                    <TableCell
                                        colSpan={columns.length - 1}
                                        className={'text-red-600'}
                                    >
                                        {t('master.tenant_failed')}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {showEmptyState ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        align={'center'}
                                        className={'text-foreground/60'}
                                        sx={{ py: 8 }}
                                    >
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                children
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component={'div'}
                        count={totalCount}
                        page={controls.page}
                        onPageChange={controls.handlePageChange}
                        rowsPerPage={controls.rowsPerPage}
                        onRowsPerPageChange={controls.handleRowsPerPageChange}
                        rowsPerPageOptions={controls.rowsPerPageOptions}
                        labelRowsPerPage={t('master.pagination.rows_per_page')}
                        labelDisplayedRows={({ from, to, count }) =>
                            t('master.pagination.displayed_rows', { from, to, count })
                        }
                    />
                </div>
            </Box>
        </Container>
    );
};

export default MasterListScaffold;
